import { Divider, Grid, Typography } from "@mui/material";
import { WASTE_CONTAINER_TYPES } from "encamp-shared/src/constants/wasteContainerTypes";
import {
  FacilityAccumulationAreaOptionsQuery,
  FacilityWasteStreamOptionsQuery,
  GetWasteContainerQuery,
  UnitType,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

export type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

export type WasteStreamOption = NonNullable<
  NonNullable<FacilityWasteStreamOptionsQuery["facility"]>["wasteStreams"]
>["items"][number];

export type AreaOption = NonNullable<
  NonNullable<FacilityAccumulationAreaOptionsQuery["facility"]>["accumulationAreas"]
>["items"][number];

/**
 * The area picker mixes the real areas with a synthetic "create a new area"
 * entry. A discriminated union keeps the create entry out of the AreaOption
 * space entirely, so it can never collide with a real area id or leak into
 * the form value.
 */
export type AreaPickerOption = { kind: "area"; area: AreaOption } | { kind: "create" };

export type WasteContainerFormValues = {
  facilityId: string;
  containerLabel: string;
  containerType: string;
  capacity: string;
  capacityUnit: UnitType | null;
  isPermanent: boolean;
  isFlowThrough: boolean;
  wasteStreamId: string | null;
  accumulationAreaId: string | null;
  initialAmount: string;
  initialAmountUnit: UnitType | null;
  isEstimate: boolean;
  accumulationStartDate: string | null;
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  estimatedPickupDate: string | null;
  /** Create only, and only when the form asked: whether this container joins
   * one of the facility's running episodic events. Separate from the id below
   * so unchecking keeps the chosen event around rather than clearing it. */
  attachToEpisodicEvent: boolean;
  episodicEventId: string | null;
};

export const CONTAINER_TYPE_SELECT_ITEMS = WASTE_CONTAINER_TYPES.map((type) => ({
  value: type.code,
  display: `${type.label} (${type.code})`,
}));

export function areaTypeLabel(type: WasteAccumulationAreaType): string {
  return type === WasteAccumulationAreaType.Central ? "Central" : "Satellite";
}

/**
 * A fact the form shows but never asks for — the facility's name, its EPA id,
 * the generator status its deadline derives from.
 *
 * Rendered as text rather than a disabled input: a greyed-out field invites
 * the reader to look for the way to enable it, and there isn't one. These
 * come from the facility record.
 */
export function ReadOnlyFact({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value || "Not on file"}</Typography>
    </Grid>
  );
}

/**
 * A labelled group of fields.
 *
 * The form asks for five unrelated things — where the container is, what it is,
 * what is in it, where it sits, and how much — and read as one flat column they
 * are hard to scan. Grouping them under the same overline the waste pages use
 * for their sections gives each question a heading.
 */
export function FormSection({
  label,
  children,
  first = false,
}: {
  label: string;
  children: ReactNode;
  first?: boolean;
}) {
  return (
    <Grid size={{ xs: 12 }}>
      {!first && <Divider sx={{ mb: 2 }} />}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, letterSpacing: ".08em" }}
      >
        {label}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Grid>
  );
}
